/**
 * The recitation engine's pure half — §5.
 *
 * ROOT CAUSE OF THE HISTORICAL BREAKAGE: six independent effects each wrote to the
 * audio element, each with its own dependency array, and any two firing in an
 * unexpected order produced a bug that looked like a logic error but was an ordering
 * error. The structural fix is one reducer, one reconciler, one observer. This file
 * is the reducer (§5.4a) and the observer's decision table (§5.5). Neither touches an
 * audio element, a store, or a clock — so T8–T30's logic is testable in node.
 */
import { TOTAL_PAGES } from './constants'
import { ayahAtMs, ayahEndMs, ayahStartMs, runAt, surahRuns } from './audio'
import { parseVerseKey, verseKey } from './navigation'
import type { SurahAudio } from './types'

export type QueueKind = 'page' | 'passage'

export interface RepeatSettings {
  /** 1 = once, Infinity = forever. */
  perAyah: number
  perPassage: number
}

export interface PlayerState {
  queue: string[]
  /**
   * LOAD-BEARING. A `page` queue is an open-ended reading position and the engine may
   * append the next page to it. A `passage` queue is a deliberate hifz drill with an
   * end the user chose — it MUST NEVER GROW, or the drill wanders off the marked
   * ayat. A tajweed exemplar is also `passage` (one ayah, must stop).
   */
  kind: QueueKind
  index: number
  /** INTENT, not the element's actual paused state. */
  isPlaying: boolean
  repeat: RepeatSettings
  /**
   * Armed as n - 1 and counted to 0. `Infinity` is the unlimited sentinel, not 0 or
   * -1, because `Infinity - 1 === Infinity` means "never stop" needs no branch
   * anywhere. A20: it is not JSON-representable, so it must never be persisted.
   */
  ayahRepeatsLeft: number
  passageRepeatsLeft: number
  /** queue[index], mirrored for subscribers. */
  currentKey: string | null
  /** A one-shot seek EVENT, consumed exactly once and cleared. Written by the reconciler only. */
  pendingSeekMs: number | null
}

export const initialPlayerState: PlayerState = {
  queue: [],
  kind: 'page',
  index: 0,
  isPlaying: false,
  repeat: { perAyah: 1, perPassage: 1 },
  ayahRepeatsLeft: 0,
  passageRepeatsLeft: 0,
  currentKey: null,
  pendingSeekMs: null,
}

const keyAt = (queue: readonly string[], index: number): string | null => queue[index] ?? null

/** Counters are armed as n - 1; Infinity - 1 is still Infinity. */
const arm = (n: number): number => Math.max(0, n - 1)

// ---------------------------------------------------------------------------
// Actions (§5.4a). Every player mutation goes through one of these. No effect
// mutates player state directly.
// ---------------------------------------------------------------------------

/**
 * A19: `ayahRepeatsLeft` is re-armed whenever the index moves FOR ANY REASON.
 * Otherwise repeats leak into the next ayah, or vanish.
 */
export function setIndex(state: PlayerState, index: number): PlayerState {
  if (state.queue.length === 0) return state
  const next = Math.min(state.queue.length - 1, Math.max(0, index))
  return {
    ...state,
    index: next,
    currentKey: keyAt(state.queue, next),
    ayahRepeatsLeft: arm(state.repeat.perAyah),
  }
}

export function play(
  state: PlayerState,
  queue: readonly string[],
  startKey: string | undefined,
  kind: QueueKind,
): PlayerState {
  // A18: play with an empty queue is a no-op. Otherwise the transport shows "pause"
  // with no sound and no way forward but a second press.
  if (queue.length === 0) return state
  const at = startKey ? queue.indexOf(startKey) : 0
  const index = at === -1 ? 0 : at
  return {
    ...state,
    queue: [...queue],
    kind,
    index,
    currentKey: keyAt(queue, index),
    isPlaying: true,
    ayahRepeatsLeft: arm(state.repeat.perAyah),
    passageRepeatsLeft: arm(state.repeat.perPassage),
  }
}

export const pause = (state: PlayerState): PlayerState => ({ ...state, isPlaying: false })

export const resume = (state: PlayerState): PlayerState =>
  state.queue.length === 0 ? state : { ...state, isPlaying: true }

export const stop = (state: PlayerState): PlayerState => ({
  ...initialPlayerState,
  repeat: state.repeat,
})

export const next = (state: PlayerState): PlayerState => setIndex(state, state.index + 1)
export const previous = (state: PlayerState): PlayerState => setIndex(state, state.index - 1)

export function setRepeat(state: PlayerState, patch: Partial<RepeatSettings>): PlayerState {
  const repeat = { ...state.repeat, ...patch }
  return {
    ...state,
    repeat,
    ayahRepeatsLeft: patch.perAyah === undefined ? state.ayahRepeatsLeft : arm(repeat.perAyah),
    passageRepeatsLeft:
      patch.perPassage === undefined ? state.passageRepeatsLeft : arm(repeat.perPassage),
  }
}

export const consumeAyahRepeat = (state: PlayerState): PlayerState => ({
  ...state,
  ayahRepeatsLeft: Math.max(0, state.ayahRepeatsLeft - 1),
})

export const consumePassageRepeat = (state: PlayerState): PlayerState => ({
  ...state,
  passageRepeatsLeft: Math.max(0, state.passageRepeatsLeft - 1),
})

/** A13: a `passage` queue never grows. */
export function extendQueue(state: PlayerState, keys: readonly string[]): PlayerState {
  if (state.kind !== 'page' || keys.length === 0) return state
  const have = new Set(state.queue)
  const added = keys.filter((k) => !have.has(k))
  if (added.length === 0) return state
  return { ...state, queue: [...state.queue, ...added] }
}

/**
 * A11/A14: trim the tail back, but NEVER cut away the ayah currently sounding.
 *
 * Because the next page is appended long before it is reached, by the time the user
 * switches auto-advance off the queue already runs past the current page. Without a
 * trim, recitation carries on into the already-appended page anyway — "the toggle
 * only works after a refresh".
 */
export function trimQueue(state: PlayerState, length: number): PlayerState {
  const cut = Math.max(state.index + 1, length)
  if (cut >= state.queue.length) return state
  return { ...state, queue: state.queue.slice(0, cut) }
}

/**
 * A12: arming a passage repeat confines BOTH ends. Auto-advance appends page after
 * page onto one queue, so by the time a passage repeat is armed the queue usually
 * STARTS several pages back — and the repeat loops via setIndex(0), which would send
 * recitation there instead of to the top of the page in hand.
 */
export function confineQueue(state: PlayerState, from: number, to: number): PlayerState {
  const lo = Math.max(0, Math.min(from, state.index))
  const hi = Math.min(state.queue.length - 1, Math.max(to, state.index))
  if (lo === 0 && hi === state.queue.length - 1) return state
  const queue = state.queue.slice(lo, hi + 1)
  const index = state.index - lo
  return { ...state, queue, index, currentKey: keyAt(queue, index) }
}

/**
 * Adopt a queue built around the ayah actually sounding. Recovery REPLACES the queue
 * rather than stepping towards it, because the stream can be pages ahead of the index.
 *
 * `isPlaying` and the passage counter are deliberately untouched: nothing about the
 * recitation changed, only our record of where it had got to.
 */
export function resyncTo(state: PlayerState, queue: readonly string[], key: string): PlayerState {
  if (queue.length === 0) return state
  const at = queue.indexOf(key)
  const index = at === -1 ? 0 : at
  return {
    ...state,
    queue: [...queue],
    index,
    currentKey: keyAt(queue, index),
    ayahRepeatsLeft: arm(state.repeat.perAyah),
  }
}

export const armSeek = (state: PlayerState, ms: number | null): PlayerState => ({
  ...state,
  pendingSeekMs: ms,
})

export const consumeSeek = (state: PlayerState): PlayerState =>
  state.pendingSeekMs === null ? state : { ...state, pendingSeekMs: null }

// ---------------------------------------------------------------------------
// The rAF tick — the exact decision table of §5.5
// ---------------------------------------------------------------------------

export type TickDecision =
  | { type: 'none' }
  /** Case 1 — THE ONLY MID-RUN SEEK. */
  | { type: 'ayah-repeat'; seekMs: number }
  /** Case 4 — follow the stream, never seek. */
  | { type: 'follow-stream'; index: number }
  /** 3a */
  | { type: 'next-in-queue'; index: number }
  /** 3b */
  | { type: 'passage-repeat' }
  /** 3c — return WITHOUT pausing; the queue can still grow. */
  | { type: 'await-growth' }
  /** 3d */
  | { type: 'stop' }

export interface TickInput {
  state: PlayerState
  /** The timing entry for the surah of the CURRENT key, or undefined. */
  audio: SurahAudio | undefined
  /** audio.currentTime * 1000 */
  ms: number
  /** L12: `ended` is the authority for "this file is finished"; the timestamp is only a hint. */
  ended: boolean
  autoAdvancePage: boolean
  /** The page the RECITER is on, used only by case 3c. */
  recitedPage: number | null
}

export function planTick(input: TickInput): TickDecision {
  const { state, audio, ms, ended, autoAdvancePage, recitedPage } = input
  const { queue, index } = state

  // Case 0
  if (!state.isPlaying || !audio || !state.currentKey) return { type: 'none' }
  const here = parseVerseKey(state.currentKey)
  if (!here) return { type: 'none' }
  const endMs = ayahEndMs(audio, here.ayah)
  if (endMs === null) return { type: 'none' }

  // L12: a surah's final timestamp routinely sits PAST the end of its own file
  // (al-Baqarah from Husary ends at 11,972,280 ms in a file 11,972,000 ms long), so
  // `ms >= endMs` is unreachable on a surah's last ayah. `ended` is the authority.
  const past = ms >= endMs || ended

  // Case 1 — ayah repeat
  if (past && state.ayahRepeatsLeft > 0) {
    const seekMs = ayahStartMs(audio, here.ayah)
    if (seekMs !== null) return { type: 'ayah-repeat', seekMs }
  }

  // Case 2
  const runs = surahRuns(queue)
  const run = runAt(runs, index)
  if (!run) return { type: 'none' }

  const runLastAyah = parseVerseKey(queue[run.to] ?? '')?.ayah ?? here.ayah

  // Case 3 — the file cannot carry us further
  if (past && here.ayah >= runLastAyah) {
    // 3a
    if (index + 1 < queue.length) return { type: 'next-in-queue', index: index + 1 }
    // 3b
    if (state.passageRepeatsLeft > 0) return { type: 'passage-repeat' }
    // 3c — NOT OPTIONAL. The end of the queue is not the end of the recitation while
    // the next page is still on its way. Resuming at the bottom of a page runs this
    // branch BEFORE the queue-extension effect has even fired: passive effects run
    // after paint, the rAF loop runs before it. Pausing here is "play does nothing".
    if (
      autoAdvancePage &&
      state.kind === 'page' &&
      recitedPage !== null &&
      recitedPage < TOTAL_PAGES
    ) {
      return { type: 'await-growth' }
    }
    // 3d
    return { type: 'stop' }
  }

  // Case 4 — ordinary progress. FOLLOW THE STREAM, NEVER SEEK (A6/L10): advancing
  // from one ayah to the next inside a passage must do nothing at all and let the
  // stream run, or the passage stops being gapless.
  const sounding = ayahAtMs(audio, ms)
  if (sounding === null || sounding === here.ayah) return { type: 'none' }

  const soundingKey = verseKey(here.surah, sounding)
  const at = queue.indexOf(soundingKey)
  if (at < run.from || at > run.to) return { type: 'none' }
  return { type: 'follow-stream', index: at }
}

// ---------------------------------------------------------------------------
// Recovery — why `resyncTo` exists and when it fires
// ---------------------------------------------------------------------------

export type ResyncDecision = { type: 'none' } | { type: 'resync'; key: string }

/**
 * L25 — THE STREAM CAN OUTRUN THE INDEX, so the index needs a way back.
 *
 * The observer polls on `requestAnimationFrame`, and rAF is PAUSED in a hidden tab
 * while media playback is not. So a backgrounded tab streams on with its decision
 * table switched off: no repeat is enforced, no queue grows, no page is followed. Come
 * back far enough along and the sounding ayah is no longer anywhere in the queue —
 * `planTick` case 4 then finds `indexOf(soundingKey) === -1`, returns `none`, and
 * returns `none` for ever. Symptom: "the highlight froze but it is still reciting."
 *
 * RECOVER ONLY WHERE THE DECISION TABLE IS STUCK. Everything `planTick` can still
 * reach is its business — an armed repeat especially. Overriding an `ayah-repeat` here
 * would silently cancel the repeat the user asked for, which is the opposite of the
 * complaint. So this defers to `planTick` and acts on `none` alone; composing the two
 * rather than re-deriving the condition is what keeps them from drifting.
 */
export function planResync(input: TickInput): ResyncDecision {
  const { state, audio, ms } = input
  if (!state.isPlaying || !audio || !state.currentKey) return { type: 'none' }

  // A13 — a passage drill has an end THE USER CHOSE. Rebuilding its queue around
  // wherever the stream happens to be would walk the drill off the marked ayat; its
  // recovery is the ordinary end-of-run ladder, which needs no queue of its own.
  if (state.kind !== 'page') return { type: 'none' }

  const here = parseVerseKey(state.currentKey)
  if (!here) return { type: 'none' }

  // One file per surah, so whatever is sounding is necessarily in THIS surah.
  const sounding = ayahAtMs(audio, ms)
  if (sounding === null) return { type: 'none' }
  const key = verseKey(here.surah, sounding)
  if (key === state.currentKey) return { type: 'none' }

  if (planTick(input).type !== 'none') return { type: 'none' }
  return { type: 'resync', key }
}

/** Apply a decision to the state. Kept next to `planTick` so the two cannot drift. */
export function applyTick(state: PlayerState, decision: TickDecision): PlayerState {
  switch (decision.type) {
    case 'ayah-repeat':
      return consumeAyahRepeat(state)
    case 'follow-stream':
    case 'next-in-queue':
      return setIndex(state, decision.index)
    case 'passage-repeat':
      return setIndex(consumePassageRepeat(state), 0)
    case 'await-growth':
    case 'none':
      return state
    case 'stop':
      return pause(state)
  }
}
